import { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IEdition, LearningActivity } from "types";
import { PollingData } from "../../components";
import { ENROLL_TYPE, LEVEL } from "../../consts";
import {
  useEditionsQuery,
  useEnrollmentMutation,
  useEnrollmentStatusQuery,
  useInvalidateDescendantsClassroomsQuery,
  useInvalidateEditionsQuery,
  useInvalidateLearningObjectQuery,
  usePatchMutation } from "../../queries";
import { useAuthStore } from "../../stores/authStore";
import { isSessionOccupiedError, formatSessionOccupiedError } from "../../utils/editionSession";
import { getError } from "../../utils/error";
import { getLearningObjectType } from "../../utils/general";

// Polling storage utilities
const EDITION_CHANGE_POLLING_KEY = "edition_change_polling";

const clearEditionChangePollingFromStorage = () => {
  sessionStorage.removeItem(EDITION_CHANGE_POLLING_KEY);
};

const getEditionChangePollingFromStorage = (): PollingData | null => {
  const data = sessionStorage.getItem(EDITION_CHANGE_POLLING_KEY);
  return data ? JSON.parse(data) : null;
};

export type EditionFeedback = {
  messageKey?: string;
  rawMessage?: string;
  descriptionKey?: string;
  iconType: "success" | "error";
};

// Risolve il testo del feedback nel componente, dove vive `t`.
export const getFeedbackText = (feedback: EditionFeedback, t: TFunction): string =>
  feedback.rawMessage ?? t(feedback.messageKey ?? "generic_error");

// descrizione opzionale del feedback dopo richiesta iscrizione
export const getFeedbackDescription = (feedback: EditionFeedback, t: TFunction): string =>
  feedback.descriptionKey ? t(feedback.descriptionKey) : "";

type EditionChangeErrorOutcome = {
  messageKey?: string;
  rawMessage?: string;
  shouldRefetch: boolean;
};

// map error after edition change
const handleEditionChangeError = (error: unknown, t: TFunction): EditionChangeErrorOutcome => {
  const parsedError = getError(error);
  const messageError = parsedError?.[0]?.message ?? "";
  if (messageError.includes("MAX_EDITION_CAPACITY_REACHED_DURING_UPDATE_ENROLLMENTS")) {
    return { messageKey: "max-capacity-reached-error", shouldRefetch: true };
  }

  if (parsedError?.[0]?.name === "FATAL") {
    //@ts-ignore
    const errorKey = JSON.parse(error as string)?.errors["FATAL"]?.[0]?.key;
    const fatalMessageKeys: Record<string, string> = {
      "bad.request.exception": "Status 400 - Bad request", //TODO tradurre?
      "bad.request.user.already.associated": "already-associated-error"
    };
    if (isSessionOccupiedError(messageError)) {
      return { rawMessage: formatSessionOccupiedError(messageError, t), shouldRefetch: false };
    }
    if (fatalMessageKeys[errorKey]) {
      return { messageKey: fatalMessageKeys[errorKey], shouldRefetch: false };
    }
    // fallback: messaggio grezzo dal backend, oppure errore generico
    return messageError
      ? { rawMessage: messageError, shouldRefetch: false }
      : { messageKey: "generic_error", shouldRefetch: false };
  }

  if (messageError.includes("participants maximum number")) {
    return { messageKey: "maximum-participants-error", shouldRefetch: false };
  }

  if (messageError.includes("already associated with user")) {
    return { messageKey: "already-associated-error", shouldRefetch: false };
  }

  // default generic error
  return { messageKey: "enrollment-generic-error", shouldRefetch: false };
};

// map feedback shown once the edition-change polling reaches a final status
const getEditionChangeCompletionFeedback = (
  status: "COMPLETE" | "ERROR",
  errorMessage: string | undefined,
  requiresApproval: boolean
): EditionFeedback => {
  // richiesta completata ma manca approvazione
  if (status === "COMPLETE" && requiresApproval) {
    return {
      descriptionKey: "enrollment_request_sent_description",
      iconType: "success",
      messageKey: "enrollment_request_sent_title"
    };
  }
  // richiesta accettata
  if (status === "COMPLETE") {
    return { iconType: "success", messageKey: "enrollment-submitted-success" };
  }

  if (errorMessage?.includes("[GET-ENROLLMENT-STATUS-ERROR]")) {
    return { iconType: "error", messageKey: "max-capacity-reached-error" };
  }

  // generic error
  return { iconType: "error", messageKey: "enrollment-polling-error" };
};

export const useEditions = (
  learningObject: LearningActivity | null,
  initialEditions?: IEdition[],
  // Approvazione già concessa dal responsabile su padre/oggetto (vedi isEnrollmentApprovalPending):
  // se true, un cambio edizione non deve più richiedere una nuova approvazione.
  approvalGranted = true,
  // Id del corso/percorso contenitore dell'aula quando l'aula fa parte di un corso/percorso.
  containerIds?: { courseId?: number | null, rootId?: number | null }
) => {
  const { t } = useTranslation();
  const { id, learningObjectTypology, enrollType, attributes } = learningObject ?? {};
  const { courseId, rootId } = containerIds ?? {};

  // se l'aula è dentro un percorso/corso, la prima
  // iscrizione va fatta sul contenitore, altrimenti sull'aula stessa (attività standalone).
  const idEnrollment = rootId || courseId || id || 0;
  const typologyEnrollment = rootId
    ? LEVEL.PATH
    : courseId
      ? LEVEL.COURSE
      : learningObjectTypology
        ? getLearningObjectType(learningObjectTypology)
        : "activity";

  const pollingCompletedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number>(0);

  const userId = useAuthStore(state => state.userData?.id_user) ?? 0;
  const corporateId = useAuthStore(state => state.userData?.organization_id);

  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<number | null>(null);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  const [feedback, setFeedback] = useState<EditionFeedback | null>(null); //response error feedback 

  const {
    data: editions ,
    refetch: refetchEditions,
    isFetching: editionsIsFetching
  } = useEditionsQuery({
    classroomId: id?.toString() ?? "",
    enabled: !initialEditions || initialEditions.length === 0,
    initialEditions
  });

  const {
    mutate: changeEditionMutate,
    isPending: isChangingEdition
  } = usePatchMutation({
    isToj: learningObject?.isToj ?? false,
    learningObjectId: id ?? 0,
    learningObjectTypology: learningObjectTypology ?? ""
  });

  const {
    mutate: addEnrollmentMutation,
    isPending: isEnrolling
  } = useEnrollmentMutation({
    attributesIds: attributes ?? [],
    enrollType: enrollType ?? "AUTO_ENROLL",
    learningObjectId: idEnrollment,
    learningObjectTypology: typologyEnrollment
  });

  const { invalidateEditions } = useInvalidateEditionsQuery({
    classroomId: id?.toString() ?? "",
    scrollToTop: false
  });

  const { invalidateDescendantsClassrooms } = useInvalidateDescendantsClassroomsQuery();

  const { invalidateLearningObject } = useInvalidateLearningObjectQuery({
    id: id ?? 0,
    learningObjectTypology
  });

  // Enrollment status query for edition change polling
  const {
    data: editionChangeStatusData,
    dataUpdatedAt: editionChangeStatusUpdatedAt,
    refetch: refetchEditionChangeStatus
  } = useEnrollmentStatusQuery({
    corporateId: corporateId ?? 0,
    editionId: newEditionIdForPolling ?? 0,
    enabled: false, // Disabled, use manual refetch only
    userId
  });

  const saveEditionChangePollingToStorage = (editionId: number, userId: number) => {
    sessionStorage.setItem(EDITION_CHANGE_POLLING_KEY, JSON.stringify({
      editionId,
      startTime: Date.now(),
      userId
    }));
  };

  // set api response feedback, if needed refetch editions
  const notify = useCallback((newFeedback: EditionFeedback, refetch = false) => {
    setFeedback(newFeedback);
    invalidateEditions();
    invalidateDescendantsClassrooms();
    if (refetch) refetchEditions();
  }, [invalidateEditions, invalidateDescendantsClassrooms, refetchEditions]);

  // clear response feedback 
  const clearFeedback = useCallback(() => setFeedback(null), []);

  // Avvia il polling di stato per l'edizione appena assegnata (via POST o via PATCH: il polling
  // interroga l'enrollment-status per userId+editionId, indipendentemente da quale delle due
  // chiamate ha effettivamente creato/aggiornato l'iscrizione).
  const startEditionChangePolling = useCallback((newEditionId: number) => {
    // Set the new edition ID for polling
    setNewEditionIdForPolling(newEditionId);

    // Reset polling completed flag
    pollingCompletedRef.current = false;
    pollingStartTimeRef.current = Date.now();

    // Save to storage and start polling
    saveEditionChangePollingToStorage(newEditionId, userId);
    setIsPollingEditionChange(true);

    // Invalidate to prepare for updates
    invalidateEditions();
    refetchEditions();
  }, [invalidateEditions, refetchEditions, userId]);

  // Cambio edizione (utente già iscritto a un'edizione dell'aula): solo PATCH.
  // `onMutationSuccess` scatta solo alla PATCH riuscita (mai in modo ottimistico prima della
  // risposta BE)
  const handleEditionChange = useCallback((newEditionId: number, onMutationSuccess?: () => void) => {
    changeEditionMutate(newEditionId, {
      onError: (error) => {
        const { messageKey, rawMessage, shouldRefetch } = handleEditionChangeError(error, t);
        notify({ iconType: "error", messageKey, rawMessage }, shouldRefetch);
      },
      onSuccess: () => {
        onMutationSuccess?.();
        startEditionChangePolling(newEditionId);
      }
    });
  }, [changeEditionMutate, startEditionChangePolling, notify, t]);

  // Prima iscrizione (nessuna edizione ancora assegnata): la POST iscrive all'attività/contenitore,
  // se va a buon fine si incatena la PATCH per assegnare l'edizione selezionata. `onMutationSuccess`
  // arriva solo a valle di entrambe le chiamate.
  const enrollInEdition = useCallback((newEditionId: number, onMutationSuccess?: () => void) => {
    addEnrollmentMutation(undefined, {
      onError: (error) => {
        const { messageKey, rawMessage, shouldRefetch } = handleEditionChangeError(error, t);
        notify({ iconType: "error", messageKey, rawMessage }, shouldRefetch);
      },
      onSuccess: () => handleEditionChange(newEditionId, onMutationSuccess)
    });
  }, [addEnrollmentMutation, handleEditionChange, notify, t]);

  // Polling logic with progressive intervals
  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        // Timeout reached
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        clearEditionChangePollingFromStorage();
        invalidateLearningObject();
        notify({ iconType: "error", messageKey: "enrollment-polling-error" }, true);
        return;
      }

      // Refetch enrollment status
      refetchEditionChangeStatus();

      // Increment interval by 5s if below max
      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      // Schedule next poll
      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    // First call after initial interval (5s)
    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPollingEditionChange, refetchEditionChangeStatus, notify, invalidateLearningObject]);

  // Handle edition change status response
  useEffect(() => {
    if (!editionChangeStatusData?.status) return;

    // If polling is already completed, do nothing
    if (pollingCompletedRef.current) return;

    // Only process status changes if polling is active
    if (!isPollingEditionChange) return;

    
    if (editionChangeStatusUpdatedAt < pollingStartTimeRef.current) return;

    const currentStatus = editionChangeStatusData.status;
    const currentErrorMessage = editionChangeStatusData.errorMessage;

    if (currentStatus === "IN_PROGRESS") {
      // Already polling, continue
      // Status is still in progress, keep polling
      return;
    } 
    if (currentStatus === "COMPLETE" || currentStatus === "ERROR") {
      // Mark polling as completed FIRST to avoid loops
      pollingCompletedRef.current = true;
      // Stop polling
      setIsPollingEditionChange(false);

      // Remove from sessionStorage
      clearEditionChangePollingFromStorage();

      // Show modal with result
      invalidateLearningObject();
      const requiresApproval = enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL && !approvalGranted;
      notify(
        getEditionChangeCompletionFeedback(currentStatus, currentErrorMessage, requiresApproval),
        true
      );
    }
    setIsPollingEditionChange(false);
    // NOT_ENROLLED: do nothing, normal behavior
  }, [
    editionChangeStatusData?.status,
    editionChangeStatusUpdatedAt,
    isPollingEditionChange,
    invalidateEditions,
    invalidateLearningObject,
    notify,
    refetchEditions,
    editionChangeStatusData?.errorMessage,
    enrollType,
    approvalGranted
  ]);

  // Restore polling from sessionStorage on mount
  useEffect(() => {
    const savedPolling = getEditionChangePollingFromStorage();
    if (savedPolling && savedPolling.userId === userId) {
      // Resume polling with the saved edition ID
      setNewEditionIdForPolling(savedPolling.editionId);
      pollingCompletedRef.current = false;
      pollingStartTimeRef.current = savedPolling.startTime;
      setIsPollingEditionChange(true);
    }
  }, [userId]);

  return {
    clearFeedback,
    editions,
    editionsIsFetching,
    enrollInEdition,
    feedback,
    handleEditionChange,
    invalidateDescendantsClassrooms,
    invalidateLearningObject,
    isChangingEdition,
    isEnrolling,
    isPollingEditionChange,
    refetchEditions
  };
};
